import { ReactNode, useRef, useState } from "react";

import { ArrowDownIcon } from "@/assets/icons";
import useOnClickOutside from "@/hooks/ui/useClickOutside";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

export type ButtonMenuItem = {
  name: string;
  value?: string;
  hide?: boolean;
  id?: string;
  onClick?: () => void;
  isHeader?: boolean;
};

type Props = {
  title: string;
  variant?:
    | "primary"
    | "secondary"
    | "ghost"
    | "warning-primary"
    | "warning-secondary"
    | "warning-ghost"
    | "danger-primary"
    | "danger-secondary"
    | "slate-secondary"
    | "danger-ghost";
  className?: string;
  btnContainerClassName?: string;
  menuClassName?: string;
  icon?: ReactNode;
  menu: ButtonMenuItem[];
  disabled?: boolean;
  focusItem?: string | null;
  ariaLabel?: string;
};

const ButtonMenu = ({
  title,
  variant,
  className,
  btnContainerClassName,
  menuClassName,
  icon,
  menu,
  disabled = false,
  focusItem,
  ariaLabel
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  useOnClickOutside(ref, () => setIsOpen(false), []);
  return (
    <div className={twMerge("relative", className)} ref={ref}>
      {isOpen && (
        <div
          className={twMerge(
            "absolute bottom-[44px] w-full rounded-xl border border-gainsboro bg-white",
            menuClassName
          )}
        >
          {menu.map((item) => (
            <Button
              variant="ghost"
              key={item.name}
              className={twMerge(
                "h-[36px] w-full btn-normal !rounded-xl !border-none text-left",
                item.hide && "hidden",
                focusItem && focusItem === item?.value && "bg-gainsboro",
                item.isHeader &&
                  "select-none hover:!bg-white cursor-default text-[#888888] font-semibold"
              )}
              data-testid={`btn-menu-item-${item.id}`}
              onClick={() => {
                item?.onClick?.();
                setIsOpen(false);
              }}
            >
              <span className="text-left w-full">{item.name}</span>
            </Button>
          ))}
        </div>
      )}
      <Button
        variant={variant}
        className={twMerge(
          "w-full h-[40px] text-sm !font-bold flex gap-2",
          icon && isOpen && "bg-gainsboro",
          btnContainerClassName ?? "btn-secondary"
        )}
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        data-testid="btn-menu"
        aria-label={ariaLabel}
      >
        {icon || (
          <>
            <span>{title}</span>
            <ArrowDownIcon className="w-4" />
          </>
        )}
      </Button>
    </div>
  );
};

export default ButtonMenu;
